<script lang="ts">
  import { apiClient } from '@/utils/api';
  import LinkCard from './LinkCard.svelte';
  import LinkEditModal from './LinkEditModal.svelte';
  import GroupHeader from './GroupHeader.svelte';
  import { groupedLinks, groups, links } from '../stores/editor';
  import type { EditorGroup, EditorLink, LinkFormValues } from '../types';

  type LinkResponse = { link: EditorLink };
  type LinksResponse = { links: EditorLink[] };
  type GroupResponse = { group: EditorGroup };

  const emptyForm = (): LinkFormValues => ({
    title: '',
    url: '',
    description: '',
    emoji: '',
    link_type: 'external',
    visibility: 'public',
    embed_type: '',
    link_group_id: '',
    scheduled_at: '',
    expires_at: '',
    thumbnail_url: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    is_active: true,
  });

  let modalOpen = $state(false);
  let modalMode = $state<'create' | 'edit'>('create');
  let modalError = $state('');
  let modalSaving = $state(false);
  let selectedLinkId = $state<string | null>(null);
  let formValues = $state<LinkFormValues>(emptyForm());

  let newGroupTitle = $state('');
  let newGroupEmoji = $state('');
  let groupSaving = $state(false);
  let groupError = $state('');

  let draggingId = $state<string | null>(null);
  let dropGroupId = $state<string | null>(null);
  let dropTargetLinkId = $state<string | null>(null);

  function normalizeDateTime(value?: string | null) {
    if (!value) return '';
    return value.slice(0, 16);
  }

  function setLinks(nextLinks: EditorLink[]) {
    links.set([ ...nextLinks ].sort((left, right) => left.position - right.position));
  }

  function setGroups(nextGroups: EditorGroup[]) {
    groups.set([ ...nextGroups ].sort((left, right) => left.position - right.position));
  }

  function resetDragState() {
    draggingId = null;
    dropGroupId = null;
    dropTargetLinkId = null;
  }

  function openCreateModal(groupId = '') {
    modalMode = 'create';
    selectedLinkId = null;
    modalError = '';
    formValues = { ...emptyForm(), link_group_id: groupId };
    modalOpen = true;
  }

  function openEditModal(link: EditorLink) {
    modalMode = 'edit';
    selectedLinkId = link.id;
    modalError = '';
    formValues = {
      title: link.title || '',
      url: link.url || '',
      description: link.description || '',
      emoji: link.emoji || '',
      link_type: link.link_type || 'external',
      visibility: link.visibility || 'public',
      embed_type: link.embed_type || '',
      link_group_id: link.link_group_id || '',
      scheduled_at: normalizeDateTime(link.scheduled_at),
      expires_at: normalizeDateTime(link.expires_at),
      thumbnail_url: link.thumbnail_url || '',
      utm_source: link.utm_source || '',
      utm_medium: link.utm_medium || '',
      utm_campaign: link.utm_campaign || '',
      is_active: link.is_active,
    };
    modalOpen = true;
  }

  function closeModal() {
    modalOpen = false;
    modalError = '';
    selectedLinkId = null;
  }

  function serializeLinkForm(values: LinkFormValues) {
    return {
      title: values.title.trim(),
      url: values.url.trim(),
      description: values.description.trim() || null,
      emoji: values.emoji.trim() || null,
      link_type: values.link_type,
      visibility: values.visibility,
      embed_type: values.embed_type || null,
      link_group_id: values.link_group_id || null,
      scheduled_at: values.scheduled_at || null,
      expires_at: values.expires_at || null,
      thumbnail_url: values.thumbnail_url.trim() || null,
      utm_source: values.utm_source.trim() || null,
      utm_medium: values.utm_medium.trim() || null,
      utm_campaign: values.utm_campaign.trim() || null,
      is_active: values.is_active,
    };
  }

  function parseError(errorValue: unknown, fallback: string) {
    if (!errorValue) return fallback;
    if (typeof errorValue === 'string') return errorValue;
    if (typeof errorValue === 'object') {
      const firstValue = Object.values(errorValue as Record<string, unknown>)[0];
      if (Array.isArray(firstValue)) return String(firstValue[0] || fallback);
      if (typeof firstValue === 'string') return firstValue;
    }
    return fallback;
  }

  function responseError(
    response: {
      error?: string;
      errors?: unknown;
      message?: string;
    },
    fallback: string
  ) {
    return parseError(response.errors || response.message || response.error, fallback);
  }

  function responseLink(response: { link?: EditorLink; data?: { link?: EditorLink } }) {
    return response.link || response.data?.link || null;
  }

  function responseLinks(response: { links?: EditorLink[]; data?: { links?: EditorLink[] } }) {
    return response.links || response.data?.links || null;
  }

  function responseGroup(response: { group?: EditorGroup; data?: { group?: EditorGroup } }) {
    return response.group || response.data?.group || null;
  }

  async function submitLink() {
    modalSaving = true;
    modalError = '';

    const payload = { profile_link: serializeLinkForm(formValues) };

    try {
      if (modalMode === 'create') {
        const response = await apiClient.post<LinkResponse>('/api/v1/profile_links', payload, {
          skipCaseConversion: true,
        });
        const link = responseLink(response);

        if (response.success && link) {
          setLinks([ ...$links, link ]);
          closeModal();
        } else {
          modalError = responseError(response, '링크를 생성하지 못했습니다.');
        }
      } else if (selectedLinkId) {
        const response = await apiClient.patch<LinkResponse>(`/api/v1/profile_links/${selectedLinkId}`, payload, {
          skipCaseConversion: true,
        });
        const link = responseLink(response);

        if (response.success && link) {
          setLinks($links.map((item) => (item.id === selectedLinkId ? link : item)));
          closeModal();
        } else {
          modalError = responseError(response, '링크를 수정하지 못했습니다.');
        }
      }
    } finally {
      modalSaving = false;
    }
  }

  async function deleteLink(link: EditorLink) {
    if (!confirm(`"${link.title}" 링크를 삭제할까요?`)) return;

    const response = await apiClient.delete<Record<string, never>>(`/api/v1/profile_links/${link.id}`, {
      skipCaseConversion: true,
    });

    if (response.success) {
      setLinks($links.filter((item) => item.id !== link.id));
    } else {
      modalError = responseError(response, '링크를 삭제하지 못했습니다.');
    }
  }

  async function toggleLink(link: EditorLink) {
    const response = await apiClient.patch<LinkResponse>(`/api/v1/profile_links/${link.id}`, {
      profile_link: { is_active: !link.is_active },
    }, {
      skipCaseConversion: true,
    });

    const updated = responseLink(response);
    if (response.success && updated) {
      setLinks($links.map((item) => (item.id === link.id ? updated : item)));
    } else {
      modalError = responseError(response, '링크 상태를 변경하지 못했습니다.');
    }
  }

  async function createGroup() {
    if (!newGroupTitle.trim()) return;

    groupSaving = true;
    groupError = '';
    try {
      const response = await apiClient.post<GroupResponse>('/api/v1/link_groups', {
        link_group: {
          title: newGroupTitle.trim(),
          emoji: newGroupEmoji.trim() || null,
          visibility: 'public',
        },
      }, {
        skipCaseConversion: true,
      });

      const group = responseGroup(response);
      if (response.success && group) {
        setGroups([ ...$groups, group ]);
        newGroupTitle = '';
        newGroupEmoji = '';
      } else {
        groupError = responseError(response, '그룹을 생성하지 못했습니다.');
      }
    } finally {
      groupSaving = false;
    }
  }

  async function editGroup(group: EditorGroup) {
    const title = prompt('그룹 이름', group.title);
    if (title === null) return;
    const emoji = prompt('이모지', group.emoji || '');
    if (emoji === null) return;

    const response = await apiClient.patch<GroupResponse>(`/api/v1/link_groups/${group.id}`, {
      link_group: {
        title: title.trim(),
        emoji: emoji.trim() || null,
        visibility: group.visibility,
      },
    }, {
      skipCaseConversion: true,
    });

    const updated = responseGroup(response);
    if (response.success && updated) {
      setGroups($groups.map((item) => (item.id === group.id ? updated : item)));
    } else {
      groupError = responseError(response, '그룹을 수정하지 못했습니다.');
    }
  }

  async function deleteGroup(group: EditorGroup) {
    if (!confirm(`"${group.title}" 그룹을 삭제할까요? 링크는 미분류로 이동됩니다.`)) return;

    const response = await apiClient.delete<Record<string, never>>(`/api/v1/link_groups/${group.id}`, {
      skipCaseConversion: true,
    });

    if (response.success) {
      setGroups($groups.filter((item) => item.id !== group.id));
      setLinks(
        $links.map((link) =>
          link.link_group_id === group.id ? { ...link, link_group_id: null } : link
        )
      );
    } else {
      groupError = responseError(response, '그룹을 삭제하지 못했습니다.');
    }
  }

  async function persistLinkOrder(nextLinks: EditorLink[]) {
    const previousLinks = $links;
    setLinks(nextLinks.map((link, index) => ({ ...link, position: index })));

    const reorderResponse = await apiClient.patch<LinksResponse>('/api/v1/profile_links/reorder', {
      link_ids: nextLinks.map((link) => link.id),
    }, {
      skipCaseConversion: true,
    });

    const reordered = responseLinks(reorderResponse);
    if (reorderResponse.success && reordered) {
      setLinks(reordered);
    } else {
      setLinks(previousLinks);
      resetDragState();
      modalError = responseError(reorderResponse, '링크 순서를 저장하지 못했습니다.');
    }
  }

  async function updateLinkGroup(linkId: string, targetGroupId: string | null) {
    const updateResponse = await apiClient.patch<LinkResponse>(`/api/v1/profile_links/${linkId}`, {
      profile_link: { link_group_id: targetGroupId },
    }, {
      skipCaseConversion: true,
    });

    const updated = responseLink(updateResponse);
    if (!updateResponse.success || !updated) {
      resetDragState();
      modalError = responseError(updateResponse, '링크 그룹을 변경하지 못했습니다.');
      return null;
    }

    return updated;
  }

  async function moveLink(linkId: string, targetGroupId: string | null) {
    const current = $links.find((item) => item.id === linkId);
    if (!current) return;
    if ((current.link_group_id || null) === targetGroupId) return;

    const updated = await updateLinkGroup(linkId, targetGroupId);
    if (!updated) return;
    const withoutCurrent = $links.filter((item) => item.id !== linkId);
    const nextLinks = [ ...withoutCurrent, updated ];
    await persistLinkOrder(nextLinks);
  }

  async function reorderLink(linkId: string, targetLinkId: string, targetGroupId: string | null) {
    const current = $links.find((item) => item.id === linkId);
    if (!current) return;
    if (linkId === targetLinkId) return;

    let moved = current;
    if ((current.link_group_id || null) !== targetGroupId) {
      const updated = await updateLinkGroup(linkId, targetGroupId);
      if (!updated) return;
      moved = updated;
    }

    const withoutCurrent = $links.filter((item) => item.id !== linkId);
    const targetIndex = withoutCurrent.findIndex((item) => item.id === targetLinkId);
    if (targetIndex === -1) {
      await persistLinkOrder([ ...withoutCurrent, moved ]);
      return;
    }

    withoutCurrent.splice(targetIndex, 0, moved);
    await persistLinkOrder(withoutCurrent);
  }

  function handleDragStart(linkId: string) {
    draggingId = linkId;
  }

  function handleDragEnd() {
    resetDragState();
  }

  function handleGroupDragOver(event: DragEvent, groupId: string | null) {
    event.preventDefault();
    dropGroupId = groupId ?? '__ungrouped__';
    dropTargetLinkId = null;
  }

  function handleGroupDragLeave() {
    dropGroupId = null;
  }

  function handleCardDragOver(event: DragEvent, groupId: string | null, linkId: string) {
    event.preventDefault();
    dropGroupId = groupId ?? '__ungrouped__';
    dropTargetLinkId = linkId;
  }

  async function handleDrop(groupId: string | null) {
    if (!draggingId) return;
    await moveLink(draggingId, groupId);
    resetDragState();
  }

  async function handleCardDrop(targetLinkId: string, targetGroupId: string | null) {
    if (!draggingId || draggingId === targetLinkId) return;
    await reorderLink(draggingId, targetLinkId, targetGroupId);
    resetDragState();
  }
</script>

<section class="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
  <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
    <div>
      <h2 class="text-lg font-semibold text-gray-950">링크</h2>
      <p class="mt-1 text-sm leading-6 text-gray-600">
        링크 추가, 수정, 삭제와 그룹 이동이 가능합니다. 카드를 드래그해서 그룹 또는 미분류 영역으로 바로 옮길 수 있습니다.
      </p>
    </div>
    <div class="flex items-center gap-2">
      <div class="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">{$links.length}개 링크</div>
      <button type="button" class="rounded-full bg-gray-950 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800" onclick={() => openCreateModal()}>
        링크 추가
      </button>
    </div>
  </div>

  <div class="mt-6 grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
    <div class="space-y-5">
      {#each $groupedLinks.grouped as group}
        <div
          class={`rounded-2xl border p-4 transition ${dropGroupId === group.id ? 'border-sky-400 bg-sky-50/70' : 'border-gray-200 bg-gray-50'}`}
          role="group"
          aria-label={group.title}
          ondragover={(event) => handleGroupDragOver(event, group.id)}
          ondragleave={handleGroupDragLeave}
          ondrop={(event) => {
            event.preventDefault();
            handleDrop(group.id);
          }}
        >
          <GroupHeader {group} onEdit={editGroup} onDelete={deleteGroup} />

          <div class="mt-4 space-y-3" role="list" aria-label={`${group.title} links`}>
            {#each group.links as link}
              <div
                role="listitem"
                class={dropTargetLinkId === link.id ? 'rounded-2xl ring-2 ring-sky-300 ring-offset-2 ring-offset-gray-50' : ''}
                ondragstart={() => handleDragStart(link.id)}
                ondragend={handleDragEnd}
                ondragover={(event) => handleCardDragOver(event, group.id, link.id)}
                ondrop={(event) => {
                  event.preventDefault();
                  handleCardDrop(link.id, group.id);
                }}
              >
                <LinkCard link={link} dragging={draggingId === link.id} onEdit={openEditModal} onToggle={toggleLink} onDelete={deleteLink} />
              </div>
            {:else}
              <button
                type="button"
                class="w-full rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-5 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700"
                onclick={() => openCreateModal(group.id)}
              >
                이 그룹에 첫 번째 링크 추가
              </button>
            {/each}
          </div>
        </div>
      {/each}

      <div
        class={`rounded-2xl border border-dashed p-4 transition ${dropGroupId === '__ungrouped__' ? 'border-sky-400 bg-sky-50/70' : 'border-gray-300 bg-white'}`}
        role="group"
        aria-label="Ungrouped links"
        ondragover={(event) => handleGroupDragOver(event, null)}
        ondragleave={handleGroupDragLeave}
        ondrop={(event) => {
          event.preventDefault();
          handleDrop(null);
        }}
      >
        <div class="flex items-center justify-between gap-3">
          <div>
            <div class="text-sm font-medium text-gray-900">미분류</div>
            <div class="mt-1 text-xs text-gray-500">카드를 여기로 드래그하면 그룹에서 해제됩니다.</div>
          </div>
          <button type="button" class="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50" onclick={() => openCreateModal()}>
            미분류 링크 추가
          </button>
        </div>

        <div class="mt-4 space-y-3" role="list" aria-label="Ungrouped link list">
          {#each $groupedLinks.ungroupedLinks as link}
            <div
              role="listitem"
              class={dropTargetLinkId === link.id ? 'rounded-2xl ring-2 ring-sky-300 ring-offset-2 ring-offset-white' : ''}
              ondragstart={() => handleDragStart(link.id)}
              ondragend={handleDragEnd}
              ondragover={(event) => handleCardDragOver(event, null, link.id)}
              ondrop={(event) => {
                event.preventDefault();
                handleCardDrop(link.id, null);
              }}
            >
              <LinkCard link={link} dragging={draggingId === link.id} onEdit={openEditModal} onToggle={toggleLink} onDelete={deleteLink} />
            </div>
          {:else}
            <div class="rounded-2xl bg-gray-50 px-4 py-5 text-sm text-gray-500">모든 링크가 그룹에 배치되어 있습니다.</div>
          {/each}
        </div>
      </div>
    </div>

    <aside class="space-y-4">
      <div class="rounded-3xl border border-gray-200 bg-gray-50 p-5">
        <div class="text-sm font-semibold text-gray-900">그룹 만들기</div>
        <div class="mt-4 grid gap-3">
          <input bind:value={newGroupTitle} placeholder="그룹 이름" class="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-gray-400" />
          <input bind:value={newGroupEmoji} placeholder="이모지 (선택)" class="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-gray-400" />
          {#if groupError}
            <div class="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{groupError}</div>
          {/if}
          <button type="button" class="rounded-full bg-gray-950 px-4 py-3 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60" disabled={groupSaving} onclick={createGroup}>
            {groupSaving ? '생성 중...' : '그룹 추가'}
          </button>
        </div>
      </div>

      <div class="rounded-3xl border border-gray-200 bg-white p-5">
        <div class="text-sm font-semibold text-gray-900">사용 안내</div>
        <ul class="mt-3 space-y-2 text-sm leading-6 text-gray-600">
          <li>카드를 드래그해서 순서를 변경할 수 있습니다.</li>
          <li>그룹 영역에 드롭하면 해당 그룹으로 이동됩니다.</li>
          <li>미분류 영역에 드롭하면 그룹이 해제됩니다.</li>
          <li>일정, 공개 범위, 임베드, UTM은 편집 모달에서 설정합니다.</li>
        </ul>
      </div>
    </aside>
  </div>

  <LinkEditModal
    open={modalOpen}
    mode={modalMode}
    groups={$groups}
    values={formValues}
    saving={modalSaving}
    error={modalError}
    onClose={closeModal}
    onSubmit={submitLink}
  />
</section>
